import { CompassOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { Alert, Button, Descriptions, Space, Table, Tabs, Timeline, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getCemetery, searchCemeteries } from "../cemeteries/cemeteryApi";
import { ApiProblem } from "../../api/httpClient";
import { useApiResource } from "../../api/useApiResource";
import { EmptyState } from "../../components/EmptyState";
import { ErrorState } from "../../components/ErrorState";
import { ForbiddenState } from "../../components/ForbiddenState";
import { LoadingSkeleton } from "../../components/LoadingSkeleton";
import { StaleState } from "../../components/StaleState";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateVn, formatDateTimeVn } from "../../lib/dateFormat";
import { getGrave, searchGraves, type GraveData, type GraveMartyrLinkSummary } from "./graveApi";
import { addressLevels } from "./graveAddress";
import { cardLinks } from "./graveLinks";
import {
  confidenceStatusLabel,
  graveTypeLabel,
  identityStateLabel,
  linkTypeLabel,
  linkTypeTone,
  positionStateLabel,
  positionStateTone,
} from "./graveVocabulary";
import { label } from "../../lib/labels";
import { EmbeddedGraveSceneSlot } from "./EmbeddedGraveSceneSlot";
import "./graves.css";

/**
 * SCR-037 — Hồ sơ mộ (S11.6, ACT05 chính cho định danh/liên kết; ACT06 phụ cho vật lý/bia/ảnh).
 *
 * Bố cục v2: MỘT thẻ hồ sơ (`s11-grave-profile`) gồm cột avatar 3D mở sẵn bên trái và cột định
 * danh bên phải (tiêu đề hồ sơ, trạng thái vị trí, thang định vị một dòng, facts, thẻ người
 * an táng, năm tab). Tiêu đề (mã mộ + tên nghĩa trang) nằm NGAY TRONG cột định danh chứ không
 * còn ở băng header riêng: băng đó chỉ chứa hai dòng chữ nên phần còn lại của bề ngang là
 * khoảng trống chết, còn trong thẻ thì tiêu đề đứng cùng tầng thị giác với avatar 3D.
 * Cảnh báo read-model nằm TRÊN thẻ, lịch sử an táng nằm NGOÀI thẻ và dưới nó —
 * cả hai đều thuộc lớp mặc định, không phụ thuộc tab đang chọn. Năm tab (`Bia mộ`, `Ảnh`,
 * `Liên kết liệt sĩ`, `ADN`, `Đính chính`) là progressive disclosure — "Bia mộ" là ngữ cảnh
 * DUY NHẤT dùng từ "bia" trên màn hình này (spec §6).
 *
 * Không có affordance ghi nào: không sửa geometry/lịch sử an táng, không ADN/đính chính/
 * di chuyển, không thao tác công cộng kiểu thắp hương/sổ tang (spec §5 + §8 negative).
 * `graveId` là UUID đường dẫn (định tuyến kỹ thuật) — không bao giờ render; `version`,
 * `linkId` và mọi raw enum cũng không vào accessible DOM.
 */


/**
 * Cảnh báo read-model → thông điệp nghiệp vụ. Mã cảnh báo là tập MỞ, additive; mã chưa có
 * thông điệp duyệt vẫn phải hiện cảnh báo nhưng KHÔNG lộ mã kỹ thuật ra DOM (guardrail §5.5).
 */
const WARNING_MESSAGES: Readonly<Record<string, Readonly<{ message: string; description: string }>>> = {
  INSCRIPTION_MISMATCH: {
    message: "Nội dung ghi trên mộ không khớp hồ sơ liệt sĩ liên kết",
    description: "Đối chiếu lại nội dung ghi trên mộ với hồ sơ liệt sĩ đang liên kết trước khi kết luận.",
  },
  MULTIPLE_SUSPECTED_CANDIDATES: {
    message: "Nhiều liên kết nghi vấn đang hiệu lực",
    description: "Hồ sơ mộ có nhiều hơn một liên kết nghi vấn đang hiệu lực; cần rà soát trước khi xác minh.",
  },
};

const GENERIC_WARNING: Readonly<{ message: string; description: string }> = {
  message: "Hồ sơ mộ có cảnh báo cần rà soát",
  description: "Hệ thống ghi nhận cảnh báo trên hồ sơ này; nội dung chi tiết do đơn vị nghiệp vụ cung cấp.",
};

const CHUA_GHI_NHAN = "Chưa ghi nhận";

/** Chuỗi nghiệp vụ đã trim, hoặc `null` khi vắng/blank (không dựng giá trị rỗng). */
function nonEmpty(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/**
 * Ngày nghiệp vụ `dd-mm-yyyy` lấy từ phần ngày của giá trị wire (`YYYY-MM-DD…`). Cố ý KHÔNG
 * quy đổi múi giờ: giá trị hiển thị đúng bằng ngày đơn vị quản lý đã ghi, không lệch theo
 * máy người xem. Bọc `formatDateVn` dùng chung, giữ `null` cho ca "chưa ghi nhận".
 */
function formatDate(value: string | null | undefined): string | null {
  const text = nonEmpty(value);
  return text === null ? null : formatDateVn(text);
}

/** Nhãn lịch sử an táng: ưu tiên `eventLabel` của read-model, thiếu thì humanise `eventType`. */
function intermentLabel(eventLabel: string | null | undefined, eventType: string): string {
  const label = nonEmpty(eventLabel);
  if (label !== null) return label;
  const words = eventType
    .trim()
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter((word) => word !== "");
  if (words.length === 0) return eventType;
  return words
    .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/** Trạng thái danh tính chưa xác định: vắng dữ liệu hoặc đúng mã UNKNOWN của contract. */
function isIdentityUnknown(identityState: string): boolean {
  const value = nonEmpty(identityState);
  return value === null || value.toUpperCase() === "UNKNOWN";
}

/** Biến thể trung thực của hồ sơ mộ (spec §7): rỗng / tập thể / chưa xác định danh tính. */
function hasNoIntermentData(grave: GraveData): boolean {
  const links = grave.martyrLinks ?? [];
  const timeline = grave.intermentTimeline ?? [];
  return links.length === 0 && timeline.length === 0 && nonEmpty(grave.inscriptionText) === null;
}

/** Dòng bảng liên kết: khóa hiển thị là chỉ số — `linkId` (UUID) không bao giờ vào DOM. */
type LinkRow = GraveMartyrLinkSummary & Readonly<{ __rowKey: number }>;

function linkColumns(): ColumnsType<LinkRow> {
  return [
    {
      title: "Liệt sĩ",
      dataIndex: "martyrDisplayName",
      key: "martyrDisplayName",
      render: (value: string | null | undefined, record: LinkRow) => {
        const name = nonEmpty(value);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {name !== null ? (
              // `martyrId` chỉ có mặt khi hồ sơ liệt sĩ resolve được cho người xem (backend
              // fail-closed) — vắng thì tên ở lại là chữ, không có link chết dẫn tới 404.
              <Typography.Text strong>
                {record.martyrId ? (
                  <Link to={`/martyrs/${record.martyrId}`} aria-label={`Xem liệt sĩ ${name}`}>
                    {name}
                  </Link>
                ) : (
                  name
                )}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">Chưa rõ tên liệt sĩ</Typography.Text>
            )}
            {record.martyrCode && (
              <Typography.Text type="secondary" style={{ fontSize: 11, fontFamily: "monospace" }}>
                {record.martyrCode}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Cấp bậc / Chức vụ",
      key: "rankAndPosition",
      render: (_: unknown, record: LinkRow) => {
        const rankOrPos = [record.rankName, record.positionName].filter(Boolean).join(" - ");
        return rankOrPos || <Typography.Text type="secondary">—</Typography.Text>;
      },
    },
    {
      title: "Đơn vị / Quê quán",
      key: "unitAndHometown",
      render: (_: unknown, record: LinkRow) => {
        const parts = [record.unitName, record.nativePlace].filter(Boolean).join(" · ");
        return parts || <Typography.Text type="secondary">—</Typography.Text>;
      },
    },
    {
      title: "Loại liên kết",
      dataIndex: "linkType",
      key: "linkType",
      render: (value: string, record: LinkRow) => (
        <StatusBadge
          tone={linkTypeTone(value)}
          label={linkTypeLabel(value, (record as Record<string, unknown>).linkTypeDisplayName)}
        />
      ),
    },
    {
      title: "Độ tin cậy",
      dataIndex: "confidenceStatus",
      key: "confidenceStatus",
      render: (value: string, record: LinkRow) =>
        confidenceStatusLabel(value, (record as Record<string, unknown>).confidenceStatusDisplayName),
    },
    {
      title: "Hiệu lực từ",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      render: (value: string) => formatDate(value) ?? CHUA_GHI_NHAN,
    },
    {
      title: "Hiệu lực đến",
      dataIndex: "effectiveTo",
      key: "effectiveTo",
      render: (value: string | null | undefined) => {
        const end = formatDate(value);
        return end !== null ? end : <Typography.Text type="secondary">Đang hiệu lực</Typography.Text>;
      },
    },
  ];
}

/** Tab "Bia mộ": nội dung ghi trên mộ, chỉ đọc, không có control sửa. */
function InscriptionPanel({ grave }: { readonly grave: GraveData }) {
  const inscription = nonEmpty(grave.inscriptionText);
  if (inscription === null) {
    return (
      <EmptyState
        title="Chưa ghi nhận nội dung trên mộ"
        description="Hồ sơ mộ chưa có nội dung ghi trên mộ; bổ sung theo quy trình của đơn vị quản lý."
      />
    );
  }
  return (
    <Typography.Paragraph className="s11-grave-inscription">{inscription}</Typography.Paragraph>
  );
}

/**
 * Tab "Ảnh": CHỈ metadata đã được phép. Hai khoá `photoType`/`photoCapturedAt` VẮNG khi
 * người gọi thiếu `DOCUMENT_READ`, còn present-null khi được phép nhưng chưa có ảnh — hai
 * tình huống này phải hiển thị khác nhau, và không có URL ảnh nào trên wire để dựng ảnh.
 */
function PhotoPanel({ grave }: { readonly grave: GraveData }) {
  const permitted = grave.photoType !== undefined || grave.photoCapturedAt !== undefined;
  if (!permitted) {
    return (
      <EmptyState
        title="Không có thông tin ảnh trong phạm vi được cấp"
        description="Thông tin ảnh thuộc quyền đọc tài liệu; hồ sơ này chưa được cấp phạm vi đó."
      />
    );
  }
  const photoType = nonEmpty(grave.photoType);
  if (photoType === null) {
    return (
      <EmptyState
        title="Chưa có ảnh cho mộ này"
        description="Hồ sơ mộ chưa ghi nhận ảnh; ảnh chỉ có khi đơn vị quản lý đã tải lên theo quy trình."
      />
    );
  }
  return (
    <Descriptions
      column={1}
      size="small"
      items={[
        {
          key: "photoType",
          label: "Loại ảnh",
          children: label(photoType, (grave as Record<string, unknown>).photoTypeDisplayName),
        },
        {
          key: "photoCapturedAt",
          label: "Thời điểm chụp",
          children: formatDate(grave.photoCapturedAt) ?? CHUA_GHI_NHAN,
        },
      ]}
    />
  );
}

/** Tab "Liên kết liệt sĩ": bảng typed từ `martyrLinks` — không lộ `linkId`/raw enum. */
function LinksPanel({ grave }: { readonly grave: GraveData }) {
  const links = grave.martyrLinks ?? [];
  if (links.length === 0) {
    return (
      <EmptyState
        title="Chưa có liên kết liệt sĩ"
        description="Hồ sơ mộ chưa ghi nhận liên kết với liệt sĩ nào."
      />
    );
  }
  const rows: LinkRow[] = links.map((link, index) => ({ ...link, __rowKey: index }));
  return (
    <Table<LinkRow>
      rowKey="__rowKey"
      size="small"
      columns={linkColumns()}
      dataSource={rows}
      pagination={false}
    />
  );
}

/**
 * Tab chưa có đơn vị sở hữu (ADN, Đính chính): nói thật là chưa khả dụng — không bịa dòng,
 * không ẩn tab, không render control ghi (spec §5 "tab thiếu owner hiện disabled/empty trung thực").
 */
function UnavailablePanel({ title, description }: {
  readonly title: string;
  readonly description: string;
}) {
  return <EmptyState title={title} description={description} />;
}

/**
 * Thang định vị của hồ sơ mộ trong thẻ: các cấp có THẬT trong dữ liệu, cấp cuối là ô
 * (`positionCode`). Không suy diễn cấp nào là "khu/lô/hàng" — số cấp phụ thuộc cây section
 * của từng nghĩa trang (spec §3.1). Vắng dữ liệu thì nói thật bằng `CHUA_GHI_NHAN`.
 *
 * Một dòng ngang nối bằng `›`: bốn dòng thụt lề cũ để trống nửa phải của cột rộng.
 */
function AddressLadder({ grave }: { readonly grave: GraveData }) {
  const levels = addressLevels(grave.sectionLabel, grave.positionCode);

  if (levels.length === 0) {
    return <p className="s11-grave-ladder-empty">{CHUA_GHI_NHAN}</p>;
  }

  return <p className="s11-grave-ladder">{levels.map((level) => level.label).join(" › ")}</p>;
}

/** Ba dữ kiện nghiệp vụ thiết yếu của hồ sơ mộ — vắng dữ liệu thì nói thật, không bịa giá trị. */
function GraveFacts({ grave }: { readonly grave: GraveData }) {
  return (
    // Mỗi cặp bọc trong `<div>` (hợp lệ trong `<dl>`): hàng ngang dùng `flex-wrap` nên nếu để
    // `dt`/`dd` trực tiếp làm flex item thì một cặp có thể bị ngắt qua hai dòng.
    <dl className="s11-grave-facts">
      <div>
        <dt>Loại mộ</dt>
        <dd>{graveTypeLabel(grave.graveType, (grave as Record<string, unknown>).graveTypeDisplayName)}</dd>
      </div>
      <div>
        <dt>Danh tính</dt>
        <dd>
          {nonEmpty(grave.identityState) !== null
            ? identityStateLabel(grave.identityState, (grave as Record<string, unknown>).identityStateDisplayName)
            : CHUA_GHI_NHAN}
        </dd>
      </div>
      <div>
        <dt>Tình trạng vật lý</dt>
        <dd>{nonEmpty(grave.physicalCondition) ?? CHUA_GHI_NHAN}</dd>
      </div>
    </dl>
  );
}

/**
 * Thẻ NGƯỜI AN TÁNG — kéo định danh từ tab thứ 3 lên lớp luôn hiển thị (spec §3.2).
 * Chỉ hiện liên kết ĐANG hiệu lực; danh sách đầy đủ (kể cả đã kết thúc) vẫn ở tab.
 * Dùng chỉ số làm `key` để không đưa `linkId` vào cây React — cùng quy ước với `LinksPanel`.
 *
 * KHÔNG dùng `.s11-grave-panel`: thẻ hồ sơ đã là khung có viền, vẽ thêm một hộp nữa bên trong
 * là hình chữ nhật lồng nhau. Khối này chỉ ngăn với phần trên bằng đường kẻ, như `.s11-grave-facts`.
 */
function IdentityPanel({ grave }: { readonly grave: GraveData }) {
  const { shown, remaining } = cardLinks(grave.martyrLinks);
  return (
    <section className="s11-grave-person-panel" aria-label="Người an táng">
      <Typography.Title level={3} className="s11-grave-panel-title">
        Người an táng
      </Typography.Title>
      {shown.length === 0 ? (
        <EmptyState
          title="Chưa có liên kết liệt sĩ đang hiệu lực"
          description="Hồ sơ mộ chưa ghi nhận liên kết đang hiệu lực với liệt sĩ nào."
        />
      ) : (
        <ul className="s11-grave-person-list">
          {shown.map((link, index) => {
            const name = nonEmpty(link.martyrDisplayName);
            const typeTone = linkTypeTone(link.linkType);
            const rankOrPos = [link.rankName, link.positionName].filter(Boolean).join(" - ");
            return (
              <li key={index} className="s11-grave-person-item">
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {name !== null ? (
                    <Typography.Text strong style={{ fontSize: 14 }}>
                      {link.martyrId ? (
                        <Link to={`/martyrs/${link.martyrId}`} aria-label={`Xem liệt sĩ ${name}`}>
                          {name}
                        </Link>
                      ) : (
                        name
                      )}
                    </Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">Chưa rõ tên liệt sĩ</Typography.Text>
                  )}
                  {rankOrPos && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600 }}>Chức vụ/Cấp bậc:</span> {rankOrPos}
                    </Typography.Text>
                  )}
                  {link.unitName && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600 }}>Đơn vị:</span> {link.unitName}
                    </Typography.Text>
                  )}
                  {link.nativePlace && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600 }}>Quê quán:</span> {link.nativePlace}
                    </Typography.Text>
                  )}
                  {(link.birthYear || link.sacrificeDate) && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {link.birthYear ? `Năm sinh: ${link.birthYear}` : ""}
                      {link.birthYear && link.sacrificeDate ? " · " : ""}
                      {link.sacrificeDate ? `Hy sinh: ${formatDate(link.sacrificeDate) ?? link.sacrificeDate}` : ""}
                    </Typography.Text>
                  )}
                </div>
                <div className="s11-grave-person-meta">
                  <StatusBadge
                    tone={typeTone}
                    label={linkTypeLabel(link.linkType, (link as Record<string, unknown>).linkTypeDisplayName)}
                  />
                  <Typography.Text type="secondary">
                    {confidenceStatusLabel(link.confidenceStatus, (link as Record<string, unknown>).confidenceStatusDisplayName)}
                    {" · "}
                    {formatDate(link.effectiveFrom) ?? link.effectiveFrom}
                    {/* `cardLinks` chỉ trả liên kết ĐANG hiệu lực (`effectiveTo` vắng/null) nên
                        không có nhánh "đã kết thúc" ở đây; danh sách đầy đủ nằm ở tab thứ 3. */}
                    {" → nay"}
                  </Typography.Text>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {remaining > 0 ? (
        <Typography.Paragraph type="secondary" className="s11-grave-person-more">
          còn {remaining} liệt sĩ khác — xem tab Liên kết liệt sĩ
        </Typography.Paragraph>
      ) : null}
    </section>
  );
}

/**
 * Đường tải DUY NHẤT tới engine 3D: `import()` ĐỘNG có specifier literal nên bundler tách riêng
 * chunk 3D — trang này không có import TĨNH nào từ feature `cemetery-3d`, nhờ đó `three`/R3F/
 * GLTFLoader không vào bundle khởi tạo của khu quản trị.
 *
 * Khai báo ở cấp module (không phải arrow inline trong JSX): host S11.6 gọi loader trong
 * `useEffect` ngay khi mount, nên identity của prop phải ổn định — arrow inline sẽ đổi identity
 * mỗi lần trang re-render và kéo theo một lượt tải mới.
 */
const loadEmbeddedGraveScene = () => import("../cemetery-3d/EmbeddedGraveScene");

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function GraveDetailPage() {
  const navigate = useNavigate();
  // `graveId` có thể là UUID đường dẫn hoặc mã mộ (graveCode) khi điều hướng từ hồ sơ liệt sĩ.
  const params = useParams<{ graveId?: string; id?: string }>();
  const graveId = (params.graveId ?? params.id ?? "").trim();
  const { state, refetch } = useApiResource(
    async (signal) => {
      if (!graveId) {
        throw new ApiProblem(404, {
          type: "urn:ncc:error:not-found",
          title: "Không tìm thấy",
          status: 404,
          code: "COMMON_NOT_FOUND",
          detail: "Hồ sơ mộ không tồn tại",
          instance: `/graves/${encodeURIComponent(graveId)}`,
          requestId: "client-not-found",
          timestamp: new Date().toISOString(),
        });
      }
      if (UUID_REGEX.test(graveId)) {
        return getGrave(graveId, signal);
      }
      const searchResult = await searchGraves({ q: graveId }, signal);
      const match =
        searchResult.rows.find(
          (row) => row.graveCode.toLowerCase() === graveId.toLowerCase(),
        ) ?? searchResult.rows[0];
      if (!match || !match.graveId) {
        throw new ApiProblem(404, {
          type: "urn:ncc:error:not-found",
          title: "Không tìm thấy",
          status: 404,
          code: "COMMON_NOT_FOUND",
          detail: `Không tìm thấy mộ với mã ${graveId}`,
          instance: `/graves/${encodeURIComponent(graveId)}`,
          requestId: "client-not-found",
          timestamp: new Date().toISOString(),
        });
      }
      return getGrave(match.graveId, signal);
    },
    [graveId],
  );

  const grave = state.status === "ready" || state.status === "refreshing" || state.status === "stale"
    ? state.data
    : null;

  const [resolvedCemeteryId, setResolvedCemeteryId] = useState<string | null>(null);

  useEffect(() => {
    if (grave && !UUID_REGEX.test(graveId) && grave.graveId) {
      void navigate(`/graves/${grave.graveId}`, { replace: true });
    }
  }, [grave, graveId, navigate]);

  useEffect(() => {
    if (grave?.cemeteryId) {
      setResolvedCemeteryId(grave.cemeteryId);
      return;
    }
    const name = grave?.cemeteryName?.trim();
    if (!name) {
      setResolvedCemeteryId(null);
      return;
    }
    const controller = new AbortController();
    void searchCemeteries({ q: name }, controller.signal)
      .then((res) => {
        const matched = res.rows.find((r) => r.name.toLowerCase() === name.toLowerCase())
          ?? res.rows[0];
        if (matched?.cemeteryId) {
          setResolvedCemeteryId(matched.cemeteryId);
        }
      })
      .catch(() => {
        // fail-soft
      });
    return () => controller.abort();
  }, [grave?.cemeteryId, grave?.cemeteryName]);

  const effectiveCemeteryId = grave?.cemeteryId ?? resolvedCemeteryId;
  const cemeteryRoute = effectiveCemeteryId
    ? `/cemeteries/${encodeURIComponent(effectiveCemeteryId)}`
    : grave?.cemeteryName
      ? `/cemeteries?q=${encodeURIComponent(grave.cemeteryName.trim())}`
      : "/cemeteries";

  const [cemeterySlug, setCemeterySlug] = useState<string | null>(null);

  useEffect(() => {
    if (!effectiveCemeteryId) {
      setCemeterySlug(null);
      return;
    }
    const controller = new AbortController();
    void getCemetery(effectiveCemeteryId, controller.signal)
      .then((cemetery) => {
        setCemeterySlug(cemetery.publicSlug ?? null);
      })
      .catch(() => {
        // fail-soft
      });
    return () => controller.abort();
  }, [effectiveCemeteryId]);

  const effectiveSlug = cemeterySlug ?? (
    grave?.cemeteryName?.toLowerCase().includes("đường 9") || grave?.cemeteryName?.toLowerCase().includes("demo")
      ? "nt-demo-001"
      : null
  );
  const warnings = grave?.warnings ?? [];
  const timeline = grave?.intermentTimeline ?? [];
  const scenePositionCode = nonEmpty(grave?.positionCode);
  const sceneGraveType = nonEmpty(grave?.graveType);

  return (
    <div className="s11-grave-detail">
      {/* Không còn `PageHeading`: băng header rời đã bỏ vì nó chỉ chứa tiêu đề hồ sơ nên phần
          lớn bề ngang là khoảng trống; tiêu đề chuyển vào cột định danh của thẻ bên dưới. */}

      {/* Cảnh báo read-model: lớp mặc định, không phụ thuộc tab đang chọn. */}
      {warnings.map((code, index) => {
        const text = WARNING_MESSAGES[code] ?? GENERIC_WARNING;
        return (
          <Alert
            key={`${code}-${String(index)}`}
            type="warning"
            showIcon
            message={text.message}
            description={text.description}
          />
        );
      })}

      {/* Biến thể hồ sơ: mộ tập thể và chưa xác định danh tính không được trông như mộ cá nhân. */}
      {grave?.graveType === "COLLECTIVE" ? (
        <Alert
          type="info"
          showIcon
          message="Mộ tập thể"
          description="Mộ tập thể ghi nhận nhiều liệt sĩ; danh tính từng người chỉ xác định được qua liên kết ở tab “Liên kết liệt sĩ”."
        />
      ) : null}
      {grave !== null && isIdentityUnknown(grave.identityState) ? (
        <Alert
          type="warning"
          showIcon
          message="Chưa xác định danh tính người an táng"
          description="Hồ sơ mộ chưa xác định được danh tính liệt sĩ an táng; cần đối chiếu hồ sơ và liên kết trước khi kết luận."
        />
      ) : null}

      {state.status === "stale" ? (
        <StaleState
          {...(state.fetchedAt !== undefined
            ? { lastSyncedAt: formatDateTimeVn(state.fetchedAt) }
            : {})}
          onRefresh={refetch}
        />
      ) : null}

      {state.status === "loading" ? (
        <div role="status" aria-label="Đang tải hồ sơ mộ">
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}
      {state.status === "refreshing" ? (
        <div role="status" aria-label="Đang làm mới hồ sơ mộ">
          <LoadingSkeleton rows={3} />
        </div>
      ) : null}
      {state.status === "error" ? (
        <ErrorState
          title="Không thể tải hồ sơ mộ"
          description="Hồ sơ mộ có thể không tồn tại hoặc không thuộc phạm vi dữ liệu được cấp. Kiểm tra lại đường dẫn hoặc thử lại sau."
          {...(state.requestId !== undefined ? { requestId: state.requestId } : {})}
          onRetry={refetch}
        />
      ) : null}
      {state.status === "forbidden" ? (
        <ForbiddenState description="Hồ sơ mộ không khả dụng trong phạm vi dữ liệu được cấp." />
      ) : null}

      {grave !== null ? (
        <>
          {/* Thẻ hồ sơ (spec §3, layout v2): khung 3D là avatar mở sẵn ở cột trái, khối định danh
              và năm tab nằm cùng thẻ bên phải. Lịch sử an táng cố ý ở NGOÀI thẻ. */}
          <section className="s11-grave-profile">
            <div className="s11-grave-avatar">
              <EmbeddedGraveSceneSlot
                sceneLoader={loadEmbeddedGraveScene}
                {...(effectiveSlug ? { cemeterySlug: effectiveSlug } : {})}
                {...(scenePositionCode !== null ? { positionCode: scenePositionCode } : {})}
                {...(sceneGraveType !== null ? { graveType: sceneGraveType } : {})}
              />
            </div>

            <div className="s11-grave-identity">
              {/* Tiêu đề hồ sơ ở đầu cột định danh: mã mộ là `<h1>` của trang, tên nghĩa trang
                  là dòng phụ ngay dưới — cùng hai chuỗi và cùng thứ bậc như băng header cũ. */}
              <div className="s11-grave-title">
                <Typography.Title level={1} className="s11-grave-title-code">
                  {grave.graveCode}
                </Typography.Title>
                <p className="s11-grave-title-cemetery">
                  {grave.cemeteryName ? (
                    <Link to={cemeteryRoute} className="s11-grave-cemetery-link">
                      <EnvironmentOutlined aria-hidden="true" />
                      <span>{grave.cemeteryName}</span>
                    </Link>
                  ) : (
                    "Chưa ghi nhận nghĩa trang"
                  )}
                </p>
              </div>

              {/* Hàng trạng thái vị trí: chip bên trái, nút `Làm mới` bám góc phải. Đường về
                  nghĩa trang chỉ còn MỘT: liên kết ở dòng tên nghĩa trang dưới mã mộ. */}
              <div className="s11-grave-identity-head">
                {/* Bọc `StatusBadge` trong một khối flex: nó là inline-flex, đặt trực tiếp làm
                    con của grid/flex thì bị kéo dài hết cột thành thanh màu. */}
                <div className="s11-grave-status">
                  <StatusBadge
                    tone={grave.positionState ? positionStateTone(grave.positionState) : "neutral"}
                    label={grave.positionState
                      ? positionStateLabel(grave.positionState)
                      : "Chưa ghi trạng thái vị trí"}
                  />
                </div>
                <Space>
                  {effectiveSlug ? (
                    <Button
                      icon={<CompassOutlined />}
                      href={`/public/cemeteries/${encodeURIComponent(effectiveSlug)}/3d?grave=${encodeURIComponent(grave.graveCode)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Xem tại nghĩa trang
                    </Button>
                  ) : null}
                  <Button onClick={refetch}>Làm mới</Button>
                </Space>
              </div>

              <AddressLadder grave={grave} />
              <GraveFacts grave={grave} />

              {/* Thẻ NGƯỜI AN TÁNG: định danh người an táng ở lớp luôn hiển thị, cạnh định vị
                  không gian của cùng một hồ sơ. */}
              <IdentityPanel grave={grave} />

              <section className="s11-grave-tabs" aria-label="Nhóm thông tin chi tiết">
                <Tabs
                  defaultActiveKey="inscription"
                  items={[
                    { key: "inscription", label: "Bia mộ", children: <InscriptionPanel grave={grave} /> },
                    { key: "photos", label: "Ảnh", children: <PhotoPanel grave={grave} /> },
                    { key: "links", label: "Liên kết liệt sĩ", children: <LinksPanel grave={grave} /> },
                    {
                      key: "dna",
                      label: "ADN",
                      children: (
                        <UnavailablePanel
                          title="Chưa có dữ liệu ADN"
                          description="Nhóm thông tin ADN chưa có đơn vị sở hữu trong giai đoạn này; màn hình không có chức năng ghi."
                        />
                      ),
                    },
                    {
                      key: "corrections",
                      label: "Đính chính",
                      children: (
                        <UnavailablePanel
                          title="Chưa có thông tin đính chính"
                          description="Quy trình đính chính hồ sơ mộ chưa được cung cấp trong giai đoạn này; màn hình chỉ đọc."
                        />
                      ),
                    },
                  ]}
                />
              </section>
            </div>
          </section>

          {hasNoIntermentData(grave) ? (
            <section className="s11-grave-panel" aria-label="Thông tin an táng">
              <EmptyState
                title="Hồ sơ mộ chưa ghi nhận thông tin an táng"
                description="Chưa có liên kết liệt sĩ, lịch sử an táng hoặc nội dung ghi trên mộ cho vị trí này."
              />
            </section>
          ) : null}

          <section className="s11-grave-panel" aria-label="Lịch sử an táng">
            <Typography.Title level={3} className="s11-grave-panel-title">
              Lịch sử an táng
            </Typography.Title>
            <Typography.Paragraph type="secondary" className="s11-grave-panel-hint">
              Chỉ đọc — lịch sử an táng do đơn vị quản lý ghi nhận, không sửa được trên màn hình này.
            </Typography.Paragraph>
            {timeline.length === 0 ? (
              <EmptyState
                title="Chưa ghi nhận lịch sử an táng"
                description="Hồ sơ mộ chưa có mốc an táng/quy tập nào."
              />
            ) : (
              <Timeline
                items={timeline.map((event, index) => ({
                  key: `${event.eventDate}-${String(index)}`,
                  content: (
                    <>
                      <Typography.Text strong>
                        {intermentLabel(event.eventLabel, event.eventType)}
                      </Typography.Text>
                      <div className="s11-grave-timeline-date">
                        <Typography.Text type="secondary">
                          {formatDate(event.eventDate) ?? event.eventDate}
                        </Typography.Text>
                      </div>
                    </>
                  ),
                }))}
              />
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
